import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, onSnapshot, orderBy, query, where, doc } from 'firebase/firestore';
import SongCard from '../components/SongCard';
import Search from '../components/Search';
import Messages from '../components/Messages';
import Alerts from '../components/Alerts.jsx';
import ProfilePage from '../components/ProfilePage';
import TipModal from '../components/TipModal';

const SongFeed = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const feedContainerRef = useRef(null);
    const [feedSeed] = useState(() => Math.floor(Math.random() * 1000000000));
    const [feedMode, setFeedMode] = useState('for-you');
    const [followingArtistIds, setFollowingArtistIds] = useState([]);
    const [followingLoading, setFollowingLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => (
        new URLSearchParams(location.search).get('tab') || 'home'
    ));
    const [pendingReqCount, setPendingReqCount] = useState(0);
    const [isArtist, setIsArtist] = useState(false);
    const [unreadNotifCount, setUnreadNotifCount] = useState(0);
    const [activeSong, setActiveSong] = useState(null);
    const [showTipModal, setShowTipModal] = useState(false);
    const [quickTip, setQuickTip] = useState(null);
    const [deviceSeed] = useState(() => {
        const key = 'hubtuneQuickTipDeviceSeed';
        const saved = window.localStorage.getItem(key);
        if (saved) return Number(saved);
        const seed = Math.floor(Math.random() * 1000000000);
        window.localStorage.setItem(key, String(seed));
        return seed;
    });

    const quickTipPresets = useMemo(() => {
        const messages = [
            ['Say Awesome 😎', 5],
            ['Masterstroke 🔥', 10],
            ['Get tea ☕', 10],
            ['This is a vibe 🎶', 5],
            ['Loved it ❤️', 10],
            ['Pure magic ✨', 5],
            ['On repeat 🔁', 10],
            ['What a tune 🎧', 5],
            ['You nailed it 👏', 10],
            ['Mood booster 🌈', 5],
            ['Take a bow 🎤', 10],
            ['Deserves love 💖', 5],
            ['Too good 🚀', 10],
            ['Melody goals 🎼', 5],
            ['Big respect 🙌', 10],
            ['Vibe unlocked 🔓', 5],
            ['Keep shining 🌟', 10],
            ['Tea break ☕', 5],
            ['Fresh talent 💫', 10],
            ['Headphones on 🎵', 5],
        ];
        const songKey = String(activeSong?.id || activeSong?.title || '');
        const songHash = Array.from(songKey).reduce((sum, char) => sum + char.charCodeAt(0), 0);
        const start = Math.abs((deviceSeed + songHash * 31) % messages.length);
        return [0, 1, 2].map((offset) => {
            const [message, amount] = messages[(start + offset * 7) % messages.length];
            return { message, amount };
        });
    }, [deviceSeed, activeSong?.id, activeSong?.title]);

    const visibleSongs = useMemo(() => {
        const feedSongs = feedMode === 'following'
            ? songs.filter((song) => followingArtistIds.includes(song.artistId || song.userId || song.ownerId))
            : songs;

        if (feedMode === 'following') return feedSongs;

        return [...feedSongs].sort((first, second) => {
            const getShuffleKey = (song) => Array.from(`${feedSeed}:${song.id || ''}`).reduce(
                (hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0,
                7
            );
            return getShuffleKey(first) - getShuffleKey(second);
        });
    }, [feedSeed, feedMode, followingArtistIds, songs]);

    const changeFeedMode = (mode) => {
        setFeedMode(mode);
        setActiveSong(null);
        if (feedContainerRef.current) feedContainerRef.current.scrollTop = 0;
    };

    const handleSupportClick = () => {
        if (!auth.currentUser) {
            alert('Please sign in to tip');
            return;
        }
        if (!activeSong?.artistId || activeSong.artistId.trim().length === 0) {
            alert('Error: Cannot determine the artist for this song. Please contact support.');
            return;
        }
        setShowTipModal(true);
    };

    const handleQuickTip = (preset) => {
        if (!auth.currentUser) {
            alert('Please sign in to tip');
            return;
        }
        if (!activeSong?.artistId) {
            alert('Error: Cannot determine the artist for this song.');
            return;
        }
        setQuickTip(preset);
        setShowTipModal(true);
    };

    useEffect(() => {
        const requestedTab = new URLSearchParams(location.search).get('tab');
        const validTab = ['home', 'search', 'messages', 'notifications', 'profile'].includes(requestedTab)
            ? requestedTab
            : 'home';
        setActiveTab(validTab);
    }, [location.search]);

    useEffect(() => {
        let unsubscribeFollowing = () => {};
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            unsubscribeFollowing();
            setFollowingArtistIds([]);
            if (!user) {
                setFollowingLoading(false);
                return;
            }

            setFollowingLoading(true);
            const followingRef = collection(db, 'users', user.uid, 'following');
            unsubscribeFollowing = onSnapshot(
                followingRef,
                (snap) => {
                    setFollowingArtistIds(snap.docs.map((followingDoc) => followingDoc.id));
                    setFollowingLoading(false);
                },
                (error) => {
                    console.error('Following snapshot error', error);
                    setFollowingArtistIds([]);
                    setFollowingLoading(false);
                }
            );
        });

        return () => {
            unsubscribeAuth();
            unsubscribeFollowing();
        };
    }, []);

    const changeTab = (tab) => {
        const nextSearch = tab === 'home' ? '' : `?tab=${tab}`;
        const currentSearch = location.search || '';
        if (currentSearch === nextSearch) {
            setActiveTab(tab);
            return;
        }
        navigate(`${location.pathname}${nextSearch}`);
    };

    // Load all songs (realtime optional; here initial fetch + snapshot for updates)
    useEffect(() => {
        setLoading(true);
        const qSongs = query(collection(db, 'songs'), orderBy('createdAt', 'desc'));
        
        const unsub = onSnapshot(
            qSongs,
            (snap) => {
                const base = snap.docs.map(d => {
                    const songData = { id: d.id, ...d.data() };
                    if (!songData.artistId) {
                        console.warn('Song missing artistId:', songData.id, songData.title || songData.name);
                    }
                    return songData;
                });
                // First, ensure each Firestore document ID appears only once
                const idMap = new Map();
                base.forEach(song => {
                    if (!song.id) return;
                    // If we already have this ID, skip (shouldn't happen, but safety check)
                    if (idMap.has(song.id)) return;
                    idMap.set(song.id, song);
                });
                
                // Then remove duplicates based on songUrl (keep the first/latest one)
                const seenUrls = new Set();
                const deduped = Array.from(idMap.values()).filter(song => {
                    const songUrl = (song.songUrl || song.audioUrl || song.url || '').trim();
                    
                    // If no URL, use title+artistId as fallback key
                    if (!songUrl) {
                        const title = (song.title || song.name || '').toLowerCase().trim();
                        const artistId = song.artistId || song.userId || song.ownerId || '';
                        const fallbackKey = `${title}_${artistId}`;
                        
                        if (!fallbackKey || seenUrls.has(fallbackKey)) {
                            return false; // Duplicate, hide it
                        }
                        seenUrls.add(fallbackKey);
                        return true;
                    }
                    
                    // If URL exists, check for duplicate URLs
                    if (seenUrls.has(songUrl)) {
                        return false; // Duplicate URL, hide it
                    }
                    seenUrls.add(songUrl);
                    return true;
                });
                setSongs(deduped);
                setLoading(false);
            },
            (err) => {
                console.error('Songs snapshot error', err);
                setLoading(false);
            }
        );
        
        return () => unsub();
    }, []);

    // Observe current user's role and unreadNotifications counter
    useEffect(() => {
        if (!auth.currentUser?.uid) {
            setIsArtist(false);
            setUnreadNotifCount(0);
            return () => {};
        }
        const uref = doc(db, 'users', auth.currentUser.uid);
        const unsub = onSnapshot(
            uref,
            (snap) => {
                const data = snap.exists() ? (snap.data() || {}) : {};
                const role = data.role || '';
                setIsArtist(role === 'artist');
                if (typeof data.unreadNotifications === 'number') {
                    setUnreadNotifCount(data.unreadNotifications);
                }
            },
            (err) => {
                console.error('User snapshot error', err);
                setIsArtist(false);
            }
        );
        return () => unsub();
    }, []);

    // Track pending message requests (for artists)
    useEffect(() => {
        if (!auth.currentUser?.uid) {
            setPendingReqCount(0);
            return () => {};
        }
        const q = query(collection(db, 'messageRequests'), where('receiverId', '==', auth.currentUser.uid));
        const unsub = onSnapshot(
            q,
            (snap) => {
                let count = 0;
                snap.forEach(d => { if ((d.data()?.status || 'pending') === 'pending') count += 1; });
                setPendingReqCount(count);
            },
            (err) => {
                console.error('Message requests snapshot error', err);
                setPendingReqCount(0);
            }
        );
        return () => unsub();
    }, []);

    // Unread notifications badge fallback
    useEffect(() => {
        if (!auth.currentUser?.uid) {
            return () => {};
        }
        const q = query(collection(db, 'notifications'), where('userId', '==', auth.currentUser.uid));
        const unsub = onSnapshot(
            q,
            (snap) => {
                let computed = 0;
                snap.forEach(d => { if (!d.data()?.read) computed += 1; });
                setUnreadNotifCount(computed);
            },
            (err) => {
                console.error('Notifications snapshot error', err);
                setUnreadNotifCount(0);
            }
        );
        return () => unsub();
    }, []);

    const renderContent = () => {
        switch (activeTab) {
            case 'messages':
                return <Messages />;
            case 'search':
                return <Search />;
            case 'notifications':
                return <Alerts embedded />;
            case 'profile':
                return <ProfilePage />;
            default:
                return (
                    <>
                        {/* Header - Fixed at top */}
                        <div className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/10 px-4 py-4">
                            <div className="flex items-center justify-between w-full max-w-lg mx-auto">
                                <h1 className="text-xl font-bold text-white">Song Feed</h1>
                                <button 
                                    className="p-2 rounded-full hover:bg-white/10 transition" 
                                    title="Search"
                                    onClick={() => setActiveTab('search')}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="w-5 h-5 text-white/90"
                                    >
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="m21 21-4.35-4.35" />
                                    </svg>
                                </button>
                            </div>
                            <div role="tablist" aria-label="Song feed" className="mx-auto mt-3 grid w-full max-w-lg grid-cols-2 rounded-lg border border-white/10 bg-white/5 p-1">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={feedMode === 'for-you'}
                                    onClick={() => changeFeedMode('for-you')}
                                    className={`rounded-md px-3 py-2 text-sm font-semibold transition ${feedMode === 'for-you' ? 'bg-white text-black' : 'text-white/65 hover:text-white'}`}
                                >
                                    ❤️ For You
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={feedMode === 'following'}
                                    onClick={() => changeFeedMode('following')}
                                    className={`rounded-md px-3 py-2 text-sm font-semibold transition ${feedMode === 'following' ? 'bg-white text-black' : 'text-white/65 hover:text-white'}`}
                                >
                                    👥 Following
                                </button>
                            </div>
                        </div>

                        {/* Feed Container - Reel-style scrolling with snap */}
                        <div ref={feedContainerRef} className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-smooth">
                            {loading && (
                                <div className="h-screen snap-start snap-always flex items-center justify-center">
                                    <div className="text-white/70">Loading songs...</div>
                                </div>
                            )}
                            {!loading && songs.length === 0 && (
                                <div className="h-screen snap-start snap-always flex items-center justify-center">
                                    <div className="text-white/60">No songs yet.</div>
                                </div>
                            )}
                            {!loading && feedMode === 'following' && followingLoading && (
                                <div className="h-screen snap-start snap-always flex items-center justify-center">
                                    <div className="text-white/70">Loading followed artists...</div>
                                </div>
                            )}
                            {!loading && feedMode === 'following' && !followingLoading && songs.length > 0 && visibleSongs.length === 0 && (
                                <div className="h-screen snap-start snap-always flex items-center justify-center px-6 text-center">
                                    <div className="text-white/60">No songs from artists you follow yet.</div>
                                </div>
                            )}
                            {!loading && visibleSongs.map((song, index) => {
                                const cardKey = `song-${song.id || index}`;
                                return (
                                    <SongCard
                                        key={cardKey}
                                        song={song}
                                        showTipButton={false}
                                        feedHeader
                                        onActiveChange={setActiveSong}
                                    />
                                );
                            })}
                        </div>

                        {/* Fixed Tip CTA - only one for the active song */}
                        {activeTab === 'home' && activeSong && (
                            <>
                                <div className="song-card-quick-tips fixed bottom-[10rem] left-0 right-0 z-40 px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                                    <div className="mx-auto grid w-full max-w-lg grid-cols-3 gap-2">
                                        {quickTipPresets.map((preset) => (
                                            <button
                                                key={`${preset.message}-${preset.amount}`}
                                                type="button"
                                                onClick={() => handleQuickTip(preset)}
                                                className="quick-tip-bubble px-1 text-center text-sm font-bold text-yellow-200 transition active:scale-95"
                                            >
                                                <span className="quick-tip-amount" aria-label={`Tip ₹${preset.amount}`}>
                                                    ₹{preset.amount}
                                                </span>
                                                <span className="quick-tip-label block">{preset.message}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="song-card-tip-cta fixed bottom-16 left-0 right-0 z-40 px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                                    <div className="w-full max-w-lg mx-auto">
                                        <button
                                            className="w-full rounded-xl bg-gradient-to-br from-[#ff3b3b] to-[#ff0000] px-4 py-4 text-base font-semibold text-white shadow-[0_8px_30px_rgba(255,0,0,0.35)] ring-1 ring-red-500/40 active:scale-[0.99] transition"
                                            onClick={handleSupportClick}
                                        >
                                            Be the Next Supporter ❤️ Tip Now
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Tip Modal */}
                        {showTipModal && activeSong && (
                            <TipModal
                                key={`tip-modal-${activeSong.id}-${activeSong.artistId}`}
                                artistId={activeSong.artistId}
                                artistName={activeSong.artist || 'Artist'}
                                songId={activeSong.id}
                                autoOpened
                                initialAmount={quickTip?.amount || ''}
                                initialMessage={quickTip?.message || ''}
                                autoPay={Boolean(quickTip)}
                                onClose={() => {
                                    setShowTipModal(false);
                                    setQuickTip(null);
                                }}
                            />
                        )}
                    </>
                );
        }
    };

    return (
        <div className="h-screen w-full bg-black text-white overflow-hidden flex flex-col">
            {/* Content Area - scrollable for Search/Profile/Messages/Notifications (Instagram-like); home has internal snap-scroll */}
            <div className={`flex-1 min-h-0 pb-16 ${activeTab === 'home' ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden scroll-smooth'}`}>
                {renderContent()}
            </div>

            {/* Bottom Navigation Bar */}
            <nav className="fixed bottom-0 left-0 right-0 z-[100] h-16 bg-[#1b1b1b] border-t border-[#2c2c2c] grid grid-cols-5 gap-0 px-1 shadow-[0_-2px_22px_rgba(255,0,0,0.2)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                <button
                    type="button"
                    className={`flex flex-col items-center justify-center gap-1 bg-transparent border-none text-[#bbb] text-base cursor-pointer transition-colors hover:text-white w-full h-full ${
                        activeTab === 'home' ? 'text-[#ff2a2a]' : ''
                    }`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeTab('home');
                    }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <span className="text-xl">🏠</span>
                    <span className="text-[0.7rem]">Home</span>
                </button>
                <button
                    type="button"
                    className={`flex flex-col items-center justify-center gap-1 bg-transparent border-none text-[#bbb] text-base cursor-pointer transition-colors hover:text-white w-full h-full ${
                        activeTab === 'search' ? 'text-[#ff2a2a]' : ''
                    }`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeTab('search');
                    }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <span className="text-xl">🔎</span>
                    <span className="text-[0.7rem]">Search</span>
                </button>
                <button
                    type="button"
                    className={`flex flex-col items-center justify-center gap-1 bg-transparent border-none text-[#bbb] text-base cursor-pointer transition-colors hover:text-white relative w-full h-full ${
                        activeTab === 'messages' ? 'text-[#ff2a2a]' : ''
                    }`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeTab('messages');
                    }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <div className="relative">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 11.5a8.38 8.38 0 0 1-1.9 5.4 8.5 8.5 0 0 1-6.6 3.1 8.38 8.38 0 0 1-5.4-1.9L3 21l1.9-4.1A8.38 8.38 0 0 1 3 11.5 8.5 8.5 0 0 1 6.1 4.9 8.38 8.38 0 0 1 11.5 3h.5a8.48 8.48 0 0 1 9 8.5Z" />
                        </svg>
                        {isArtist && pendingReqCount > 0 && (
                            <span className="absolute -top-2 -right-3 text-[10px] px-1.5 py-0.5 rounded-full bg-[#ff2a2a] text-white shadow-[0_0_8px_rgba(255,0,0,0.6)]">
                                {pendingReqCount}
                            </span>
                        )}
                    </div>
                    <span className="text-[0.7rem]">Comments</span>
                </button>
                <button
                    type="button"
                    className={`flex flex-col items-center justify-center gap-1 bg-transparent border-none text-[#bbb] text-base cursor-pointer transition-colors hover:text-white relative w-full h-full ${
                        activeTab === 'notifications' ? 'text-[#ff2a2a]' : ''
                    }`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeTab('notifications');
                    }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <div className="relative">
                        <span className="text-xl">🔔</span>
                        {unreadNotifCount > 0 && (
                            <span className="absolute -top-2 -right-3 text-[10px] px-1.5 py-0.5 rounded-full bg-[#ff2a2a] text-white shadow-[0_0_8px_rgba(255,0,0,0.6)]">
                                {unreadNotifCount}
                            </span>
                        )}
                    </div>
                    <span className="text-[0.7rem]">Alerts</span>
                </button>
                <button
                    type="button"
                    className={`flex flex-col items-center justify-center gap-1 bg-transparent border-none text-[#bbb] text-base cursor-pointer transition-colors hover:text-white w-full h-full ${
                        activeTab === 'profile' ? 'text-[#ff2a2a]' : ''
                    }`}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeTab('profile');
                    }}
                    style={{ pointerEvents: 'auto' }}
                >
                    <span className="text-xl">👤</span>
                    <span className="text-[0.7rem]">Profile</span>
                </button>
            </nav>
        </div>
    );
};

export default SongFeed;